/**
 * ProductList
 * ------------------------------------------
 * Reusable admin component that:
 *  - Lists all products in a grid
 *  - Lets admin filter by name, brand, and category
 *  - Provides Edit / Delete actions for each product
 */

import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { Product } from "./product/types";

type Props = {
  products: Product[];
  onDelete: (id: string) => void; // ✅ UUID
  onEdit: (product: Product) => void;
  brands: { id: number; name: string }[];
  categories: { id: number; name: string }[];
};

const ProductList = ({ products, onDelete, onEdit, brands, categories }: Props) => {
  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<number | "">("");
  const [selectedCategory, setSelectedCategory] = useState<number | "">("");
  const [openBrandIds, setOpenBrandIds] = useState<number[]>([]);

  // Filter products by name, brand, and category
  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const matchesSearch = product.name
          .toLowerCase()
          .includes(search.toLowerCase());
        const matchesBrand =
          selectedBrand === "" || product.brandId === selectedBrand;
        const matchesCategory =
          selectedCategory === "" ||
          product.categoryId === selectedCategory ||
          product.categoryIds?.includes(selectedCategory);

        return matchesSearch && matchesBrand && matchesCategory;
      }),
    [products, search, selectedBrand, selectedCategory]
  );

  const groupedProducts = useMemo(() => {
    const brandNameById = new Map(brands.map((brand) => [brand.id, brand.name]));
    const groups = new Map<
      number,
      { brandId: number; brandName: string; products: Product[] }
    >();

    filteredProducts.forEach((product) => {
      if (!groups.has(product.brandId)) {
        groups.set(product.brandId, {
          brandId: product.brandId,
          brandName: brandNameById.get(product.brandId) || String(product.brandId),
          products: [],
        });
      }

      groups.get(product.brandId)?.products.push(product);
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.brandName.localeCompare(b.brandName)
    );
  }, [brands, filteredProducts]);

  const toggleBrandGroup = (brandId: number) => {
    setOpenBrandIds((prev) =>
      prev.includes(brandId)
        ? prev.filter((id) => id !== brandId)
        : [...prev, brandId]
    );
  };

  const renderProductCard = (product: Product) => (
    <div
      key={product.id}
      className="bg-white border border-gray-200 rounded-lg shadow-md p-4 flex flex-col justify-between"
    >
      <img
        src={product.image_url || "/10E-Beauty/public/no-image.jpg"}
        alt={product.name}
        className="w-full h-40 object-contain rounded bg-gray-50 mb-4"
      />

      <div className="flex-grow">
        <h3 className="text-lg font-semibold text-[#5c2f2f]">{product.name}</h3>
        <p className="text-sm text-gray-500">${product.price}</p>
        <p className="text-sm text-gray-600">
          Brand:{" "}
          {brands.find((brand) => brand.id === product.brandId)?.name ||
            product.brandId}
        </p>
        <p className="text-sm text-gray-600">
          Categories:{" "}
          {(product.categoryIds?.length ? product.categoryIds : [product.categoryId])
            .map((id) => categories.find((category) => category.id === id)?.name || String(id))
            .join(", ")}
        </p>
        <p className="text-sm text-gray-700 mt-2 line-clamp-3">
          {product.description}
        </p>
      </div>

      <div className="flex justify-end gap-4 mt-4">
        <button
          type="button"
          onClick={() => onEdit(product)}
          className="text-[#5c2f2f] text-sm hover:underline hover:text-[#7e4242]"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(product.id)}
          className="text-[#a03535] text-sm hover:underline hover:text-[#c94646]"
        >
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-10">
      <h2 className="text-2xl font-bold text-[#5c2f2f] mb-4">All Products</h2>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 p-2 border rounded"
        />

        <select
          value={selectedBrand}
          onChange={(e) =>
            setSelectedBrand(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="p-2 border rounded"
        >
          <option value="">All Brands</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>

        <select
          value={selectedCategory}
          onChange={(e) =>
            setSelectedCategory(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="p-2 border rounded"
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Product Groups */}
      {filteredProducts.length === 0 ? (
        <p className="text-gray-600 italic">No products found.</p>
      ) : (
        <div className="space-y-3">
          {groupedProducts.map((group) => {
            const isOpen = openBrandIds.includes(group.brandId);

            return (
              <section
                key={group.brandId}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white"
              >
                <button
                  type="button"
                  onClick={() => toggleBrandGroup(group.brandId)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                  aria-expanded={isOpen}
                >
                  <span className="flex items-center gap-2 text-lg font-semibold text-[#5c2f2f]">
                    {isOpen ? (
                      <ChevronDown size={18} aria-hidden="true" />
                    ) : (
                      <ChevronRight size={18} aria-hidden="true" />
                    )}
                    <span>{group.brandName}</span>
                  </span>
                  <span className="text-sm text-gray-500">
                    {group.products.length} product
                    {group.products.length === 1 ? "" : "s"}
                  </span>
                </button>

                {isOpen && (
                  <div
                    className="grid grid-cols-1 gap-6 border-t border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-3"
                  >
                    {group.products.map(renderProductCard)}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductList;
